import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import styled from "styled-components";
import bannerImage from "../Images/banner1.png";
import Logo from "../Images/Logonew.jpeg";
import { Helmet } from "react-helmet-async";
import {
  requestEmailOtp,
  verifyEmailOtp,
  registerWithEmailOtp,
} from "./utilities/Authentication";
import { statusFailure, statusSuccess } from "./Constant";
import { Toaster, toast } from "sonner";

export default function EmailRegister() {
  const history = useHistory();
  const [step, setStep] = useState(1);
  const [errorState, setErrorState] = useState("");
  const [successState, setSuccessState] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [verifiedToken, setVerifiedToken] = useState("");
  const [otp, setOtp] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [formState, setFormState] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    userName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!expiresAt || step !== 2) return;

    const updateTimer = () => {
      const remaining = Math.max(
        0,
        Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000)
      );
      setTimeRemaining(remaining);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [expiresAt, step]);

  function formatTime(seconds) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  }

  function handleChange(e) {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value,
    });
  }

  function showHidePassword(e) {
    e.preventDefault();
    setShowPassword(!showPassword);
  }

  function validateRegistrationDetails() {
    const firstName = formState.firstName.trim();
    const lastName = formState.lastName.trim();
    const email = formState.email.trim();
    const password = formState.password;
    const confirmPassword = formState.confirmPassword;
    const emailRegex = /\S+@\S+\.\S+/;
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d).{8,64}$/;

    if (!firstName) {
      return "First name is required.";
    }

    if (!lastName) {
      return "Last name is required.";
    }

    if (!email) {
      return "Email is required.";
    }

    if (!emailRegex.test(email)) {
      return "Please enter a valid email address.";
    }

    if (!password) {
      return "Password is required.";
    }

    if (!passwordRegex.test(password)) {
      return "Password must be 8-64 characters long and include at least one letter and one number.";
    }

    if (!confirmPassword) {
      return "Confirm password is required.";
    }

    if (password !== confirmPassword) {
      return "Password and confirm password should match.";
    }

    return "";
  }

  async function requestOtp(e) {
    e.preventDefault();
    setErrorState("");
    setSuccessState("");
    const validationMessage = validateRegistrationDetails();
    if (validationMessage) {
      setErrorState(validationMessage);
      toast.error(validationMessage);
      return;
    }

    try {
      const response = await requestEmailOtp({
        email: formState.email.trim(),
        purpose: "register",
      });

      if (response.status !== statusFailure && response.data?.challengeId) {
        setChallengeId(response.data.challengeId);
        setExpiresAt(response.data.expiresAt || "");
        setStep(2);
        setSuccessState("OTP sent to your email.");
        toast.success("OTP sent to your email.");
      } else {
        setErrorState(response.message || "Unable to send OTP.");
        toast.error(response.message || "Unable to send OTP.");
      }
    } catch (error) {
      const message =
        error?.response?.data?.message || "Unable to send OTP. Please try again.";
      setErrorState(message);
      toast.error(message);
    }
  }

  async function verifyOtp(e) {
    e.preventDefault();
    setErrorState("");
    setSuccessState("");

    try {
      const response = await verifyEmailOtp({ challengeId, otp });
      if (response.status !== statusFailure && response.data?.verifiedToken) {
        setVerifiedToken(response.data.verifiedToken);
        setStep(3);
        setSuccessState("OTP verified successfully.");
        toast.success("OTP verified successfully.");
      } else {
        setErrorState(response.message || "Invalid OTP.");
        toast.error(response.message || "Invalid OTP.");
      }
    } catch (error) {
      const message = error?.response?.data?.message || "Invalid OTP.";
      setErrorState(message);
      toast.error(message);
    }
  }

  async function createAccount(e) {
    e.preventDefault();
    setErrorState("");
    setSuccessState("");

    try {
      const fullName = [
        formState.firstName.trim(),
        formState.middleName.trim(),
        formState.lastName.trim(),
      ]
        .filter(Boolean)
        .join(" ");

      const response = await registerWithEmailOtp({
        verifiedToken,
        name: fullName || formState.userName.trim() || formState.email.trim(),
        userName: formState.userName.trim(),
        email: formState.email.trim(),
        password: formState.password,
      });

      if (response.status === statusSuccess) {
        setSuccessState("Account created successfully. Please login.");
        toast.success("Account created successfully. Please login.");
        setTimeout(() => {
          history.push("/login");
        }, 1500);
      } else {
        setErrorState(response.message || "Unable to create account.");
        toast.error(response.message || "Unable to create account.");
      }
    } catch (error) {
      const message =
        error?.response?.data?.message || "Unable to create account.";
      setErrorState(message);
      toast.error(message);
    }
  }

  const fullNameDisplay = [
    formState.firstName.trim(),
    formState.middleName.trim(),
    formState.lastName.trim(),
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Root
      style={{
        background: `url(${bannerImage})`,
      }}
    >
      <Helmet>
        <title>Create Account | StoryTent</title>
        <meta name="robots" content="noindex, follow" />
        <meta name="description" content="Join StoryTent today to read, write, and share original stories and poems with a global community of creators." />
      </Helmet>
      <Toaster />
      <div className="container">
        <img className="logo" src={Logo} alt="StoryTent logo" />
        <h1>
          {step === 1
            ? "Create Account"
            : step === 2
            ? "Verify Email OTP"
            : "Complete Registration"}
        </h1>
        <p>
          {step === 1
            ? "Register with your email and password."
            : step === 2
            ? `Enter the 6-digit OTP sent to ${formState.email}.`
            : "Your account is ready to be created."}
        </p>

        {errorState ? <div className="error">{errorState}</div> : null}
        {successState ? <div className="success">{successState}</div> : null}
        {step === 2 ? (
          <div className="timer">
            OTP valid for:{" "}
            <strong>{timeRemaining > 0 ? formatTime(timeRemaining) : "Expired"}</strong>
          </div>
        ) : null}

        {step === 1 ? (
          <form onSubmit={requestOtp} onChange={handleChange} className="form">
            <input
              type="text"
              name="firstName"
              placeholder="First name *"
              value={formState.firstName}
            />
            <input
              type="text"
              name="middleName"
              placeholder="Middle name (optional)"
              value={formState.middleName}
            />
            <input
              type="text"
              name="lastName"
              placeholder="Last name *"
              value={formState.lastName}
            />
            <input
              type="text"
              name="userName"
              placeholder="Username (optional)"
              value={formState.userName}
            />
            <input
              type="email"
              name="email"
              placeholder="Email *"
              value={formState.email}
            />
            <div className="passwordField">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Password"
                value={formState.password}
              />
              <button
                className="password_show"
                type="button"
                onClick={showHidePassword}
              >
                <i
                  className={showPassword ? "fa fa-eye" : "fa fa-eye-slash"}
                  aria-hidden="true"
                ></i>
              </button>
            </div>
            <div className="passwordField">
              <input
                type={showPassword ? "text" : "password"}
                name="confirmPassword"
                placeholder="Confirm password"
                value={formState.confirmPassword}
              />
              <button
                className="password_show"
                type="button"
                onClick={showHidePassword}
              >
                <i
                  className={showPassword ? "fa fa-eye" : "fa fa-eye-slash"}
                  aria-hidden="true"
                ></i>
              </button>
            </div>
            <button type="submit" className="button">
              Send OTP
            </button>
          </form>
        ) : step === 2 ? (
          <form onSubmit={verifyOtp} className="form">
            <input
              type="text"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="Enter 6-digit OTP"
            />
            <button type="submit" className="button">
              Verify OTP
            </button>
            {timeRemaining === 0 ? (
              <div className="error">This OTP has expired. Please request a new one.</div>
            ) : null}
            <Link to="#" onClick={() => setStep(1)} className="text-link">
              Change details
            </Link>
          </form>
        ) : (
          <form onSubmit={createAccount} className="form">
            <div className="summary">
              <strong>{fullNameDisplay || formState.userName || "User"}</strong>
              <span>{formState.email}</span>
            </div>
            <button type="submit" className="button">
              Create Account
            </button>
          </form>
        )}

        <div className="footer-links">
          <Link to="/login" className="text-link">
            Back to login
          </Link>
        </div>
      </div>
    </Root>
  );
}

const Root = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background-size: cover !important;
  background-position: center !important;
  padding: 20px 16px;
  overflow-y: auto;
  box-sizing: border-box;

  .container {
    width: 100%;
    max-width: 400px;
    background-color: #0f1817;
    border-radius: 16px;
    padding: 24px 22px;
    box-shadow: -1px 6px 30px 12px rgba(0, 0, 0, 0.45);
    text-align: center;
    margin: auto;
  }

  .logo {
    width: 60px;
    height: 60px;
    object-fit: cover;
    margin-bottom: 12px;
  }

  h1 {
    color: white;
    font-size: 22px;
    margin-bottom: 8px;
  }

  p {
    color: white;
    margin-bottom: 14px;
    font-size: 14px;
  }

  .error {
    color: #ff7b7b;
    margin-bottom: 10px;
    font-size: 13px;
  }

  .success {
    color: #6be585;
    margin-bottom: 10px;
    font-size: 13px;
  }

  .timer {
    color: #f6e27a;
    margin-bottom: 10px;
    font-size: 13px;
  }

  .form {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  input {
    width: 100%;
    padding: 9px 10px;
    border-radius: 7px;
    border: 1px solid #ccc;
    font-size: 14px;
    box-sizing: border-box;
  }

  .passwordField {
    width: 100%;
    display: flex;
    background-color: white;
    border: 1px solid #ccc;
    border-radius: 7px;
    overflow: hidden;
  }

  .passwordField input {
    border: none;
    outline: none;
    font-size: 14px;
  }

  .password_show {
    background-color: white;
    border: none;
    padding: 0 10px;
  }

  .button {
    width: 100%;
    background-color: #6c63ff;
    color: white;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    border: none;
    border-radius: 7px;
    padding: 10px 12px;
  }

  .text-link {
    color: #8bb8ff;
    text-decoration: underline;
    font-size: 13px;
  }

  .footer-links {
    margin-top: 14px;
  }

  .summary {
    background: rgba(255, 255, 255, 0.06);
    color: white;
    border-radius: 10px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 3px;
    font-size: 13px;
    text-align: left;
  }
`;
